import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { Mode } from '@/types/entity-mode';
import { ServerError } from '@/types/errors/server.error';

export const GET_SESSION_QUERY_KEY = 'get-session';

type SessionQueryOptions = {
  enabled?: boolean;
};

export const useGetSessionQuery = (id?: string, mode?: Mode, options: SessionQueryOptions = {}) => {
  const { getToken } = useAuth();
  const { enabled = true } = options;

  return useQuery({
    queryKey: [GET_SESSION_QUERY_KEY, id, mode],
    queryFn: async () => {
      const token = await getToken();
      return getSession({ id: id!, token: token ?? '', mode });
    },
    retry: (failureCount, error) => {
      if (error instanceof ServerError && error.statusCode === 404) return false;
      return failureCount < 2;
    },
    enabled: !!id && enabled,
  });
};

const getSession = async ({ id, token, mode }: { id: string; token: string; mode?: Mode }): Promise<SessionDto> => {
  const url = new URL(`${import.meta.env.VITE_API_URL}/sessions/${id}`);
  if (mode) {
    url.searchParams.set('mode', mode);
  }
  return sendJSONRequest({ url: url.toString(), method: METHOD.GET, token });
};
